import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Column } from 'primereact/column'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import { Paginator, type PaginatorPageChangeEvent } from 'primereact/paginator'
import Button from '../../components/ui/Button'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { isOptimisticLock } from '../../api/apiError'
import { formatDate } from '../../lib/datetime'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useGrants } from '../../auth/useCurrentUser'
import { GRANTS } from '../../auth/grants'
import { exportPrincipleDecisions } from './api/principleDecisionApi'
import { usePrincipleDecisionMutations, usePrincipleDecisions } from './hooks/usePrincipleDecisions'
import PrincipleDecisionFormDialog from './components/PrincipleDecisionFormDialog'
import PrincipleDecisionRowActions from './components/PrincipleDecisionRowActions'
import { groupLabels } from './responsibleGroups'
import ResponsibleGroups from '../../components/ui/ResponsibleGroups'
import StakeholdersDialog from './components/StakeholdersDialog'
import PrincipleFilesDialog from './components/PrincipleFilesDialog'
import type { PrincipleDecision, PrincipleDecisionUpsert } from './types'
import '../admin/admin.css'
import './principleDecisions.css'

const PAGE_SIZE = 20

/**
 * Prensip Kararları Modülü (analiz 5) — prensip kararlarının merkezi listesi: hem toplantıdan **bağımsız**
 * hem de **toplantıya bağlı** (tutanaktan gelen) prensipler görünür ve ikisi de buradan düzenlenip silinebilir
 * (arama + sayfalama + oluştur/düzenle/soft-delete + paydaş yönetimi). Yeni kayıtlar bağımsız oluşturulur.
 * Başlık = menü adı (`nav.tutanak.principleDecisions`). Yazma aksiyonları yalnız Özel Kalem'e (MINUTES_CREATE)
 * gösterilir; asıl koruma backend'dedir.
 */
export default function PrincipleDecisionsPage() {
  const { t } = useTranslation()
  const { hasGrant } = useGrants()
  const canManage = hasGrant(GRANTS.PRINCIPLE_DECISION_MANAGE)
  // 077 — Prensip Karar No kolonu yalnız RECORD_NUMBER_VIEW yetkisinde (Özel Kalem).
  const showRecordNumber = hasGrant(GRANTS.RECORD_NUMBER_VIEW)
  const toast = useToast()

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(PAGE_SIZE)
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0 | null | undefined>(undefined)
  const sort = sortField ? `${sortField},${sortOrder === 1 ? 'asc' : 'desc'}` : undefined

  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search)
      setFirst(0)
    }, 300)
    return () => clearTimeout(id)
  }, [search])

  const list = usePrincipleDecisions({
    q: debounced,
    page: Math.floor(first / rows),
    size: rows,
    sort,
  })
  const { create, update, remove } = usePrincipleDecisionMutations()

  // `useMemo`: `?? []` her render'da yeni bir dizi üretir; `items` derin-bağlantı efektinin (aşağıda)
  // bağımlılığı olduğundan bu, efekti her render'da yeniden çalıştırırdı.
  const items = useMemo(() => list.data?.content ?? [], [list.data])
  const total = list.data?.page.totalElements ?? 0
  const submitting = create.isPending || update.isPending
  // Dar ekranda (eşik: CARD_VIEW_BREAKPOINT — lib/useMediaQuery) 6-7 kolonlu `tk-table-fit` yerine kart listesi — aynı veri/işlemler,
  // yalnız sunum dalı değişir (bkz. principleDecisions.css `.pd-card-list` — bu ekrana özgü ayrıştırılmış kopya).
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<PrincipleDecision | null>(null)
  const [skOpen, setSkOpen] = useState(false)
  const [skPrinciple, setSkPrinciple] = useState<PrincipleDecision | null>(null)

  /**
   * Derin bağlantı: `?id=` ile gelindiğinde ilgili prensip kararı **açılır** (058, spec FR-020.2).
   *
   * Prensip kararının ayrı bir detay sayfası yoktur; bildirim merkezi bu ekrana kayıt kimliğiyle
   * yönlendirir — projede zaten kullanılan kalıp (`/tickets/detail?id=`). Kayıt geçerli sayfada
   * bulunamazsa (silinmiş ya da başka sayfada) liste normal açılır, hata gösterilmez.
   *
   * `openedDeepLinkId`, aynı kimlik için pencerenin kullanıcı kapattıktan sonra tekrar açılmasını
   * engeller (liste her tazelendiğinde efekt yeniden çalışır).
   */
  const [searchParams] = useSearchParams()
  const deepLinkId = searchParams.get('id')
  const [openedDeepLinkId, setOpenedDeepLinkId] = useState<string | null>(null)

  useEffect(() => {
    if (!deepLinkId || deepLinkId === openedDeepLinkId) return
    const match = items.find((item) => String(item.id) === deepLinkId)
    if (!match) return
    setOpenedDeepLinkId(deepLinkId)
    setEditing(match)
    setFormOpen(true)
  }, [deepLinkId, openedDeepLinkId, items])

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }
  const openEdit = (row: PrincipleDecision) => {
    setEditing(row)
    setFormOpen(true)
  }
  const openStakeholders = (row: PrincipleDecision | null) => {
    setSkPrinciple(row)
    setSkOpen(true)
  }

  /** Dosyalar pop-up'ı (108) — açık karar; `null` iken kapalı. Paydaşlar'dan farklı olarak tek
   * state yeter: bu pop-up karar seçtirmiyor, satırdan açılıyor. */
  const [filesPrinciple, setFilesPrinciple] = useState<PrincipleDecision | null>(null)

  const handleMutationError = (err: unknown) => {
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
    // Diğer hatalar merkezî toast (019) ile gösterilir.
  }

  const onSubmit = (payload: PrincipleDecisionUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            notify('success', t('principleDecisions.toast.updated'))
            setFormOpen(false)
          },
          onError: handleMutationError,
        },
      )
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          notify('success', t('principleDecisions.toast.created'))
          setFormOpen(false)
        },
        onError: handleMutationError,
      })
    }
  }

  const onDelete = (row: PrincipleDecision) => {
    confirmDialog({
      header: t('principleDecisions.delete.title'),
      message: t('principleDecisions.delete.confirm', {
        number: row.recordNumber || row.description,
      }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('principleDecisions.delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', t('principleDecisions.toast.deleted')),
        }),
    })
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <PrincipleDecisionFormDialog
        visible={formOpen}
        initial={editing}
        submitting={submitting}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />
      <StakeholdersDialog
        visible={skOpen}
        principle={skPrinciple}
        principles={items}
        canManage={canManage}
        onHide={() => setSkOpen(false)}
      />
      <PrincipleFilesDialog
        decision={filesPrinciple}
        canManage={canManage}
        onHide={() => setFilesPrinciple(null)}
      />

      <PageHeader
        title={t('nav.tutanak.principleDecisions')}
        actions={
          <>
            {/* Yenile, Excel'in SOLUNDA (kullanıcı isteği 2026-08-27); önceden filtre çubuğundaydı. */}
            <RefreshButton onRefresh={() => list.refetch()} />
            {/* Dışa aktarma yönetim yetkisine bağlı DEĞİL: listeyi okuyabilen dosyayı da alabilir. */}
            <ExcelExportButton
              onExport={() => exportPrincipleDecisions(debounced, sort)}
              disabled={items.length === 0}
            />
            {canManage && (
              <Button
                icon="pi pi-plus"
                label={t('principleDecisions.actions.new')}
                onClick={openCreate}
              />
            )}
          </>
        }
      />

      <FilterBar
        className="ui-filterbar--wide-search"
        searchValue={search}
        searchPlaceholder={t('principleDecisions.searchPlaceholder')}
        searchAriaLabel={t('principleDecisions.searchPlaceholder')}
        onSearch={setSearch}
      />

      {/* Dar ekranda (eşik: CARD_VIEW_BREAKPOINT — lib/useMediaQuery) 6-7 kolonlu `tk-table-fit` yerine kart listesi — aynı veri/
          sayfalama/işlemler (paydaş/düzenle/sil), yalnız sunum dalı değişir. Kart sınıfları (`pd-card*`)
          paylaşılan `ui.css` `.ui-card-list` ailesinden bu ekrana özgü olarak AYRIŞTIRILDI (principleDecisions.css)
          — burada serbestçe değiştirilebilir, diğer ekranlardaki ortak sürüm etkilenmez. eşiğin ÜSTÜNDE bu dal
          hiç render edilmez, `tk-table-fit` aynen korunur (kullanıcı kararı 2026-08-10, aşağıdaki notta açıklanır). */}
      {isMobile ? (
        <div className="pd-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="pd-card pd-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && items.length === 0 && (
            <EmptyState
              icon="pi pi-gavel"
              title={t('principleDecisions.empty.title')}
              description={t('principleDecisions.empty.desc')}
            />
          )}
          {!list.isLoading &&
            items.map((row) => {
              // Kart dar ekrandadır: rozet yerine gruplanmış düz metin (liste adları + tekil kişiler).
              const names = groupLabels(row.responsibles ?? [])
              return (
                <div key={row.id} className="pd-card">
                  <span className="pd-card-sub">
                    {row.meetingGroupName || t('principleDecisions.columns.groupGeneral')}
                    <div className="pd-card-actions">
                      <PrincipleDecisionRowActions
                        row={row}
                        canManage={canManage}
                        onStakeholders={openStakeholders}
                        onFiles={setFilesPrinciple}
                        onEdit={openEdit}
                        onDelete={onDelete}
                      />
                    </div>
                  </span>
                  <div className="pd-card-head">
                    <span className="pd-card-title">{row.description}</span>
                    <span className="pd-card-sub">
                      {showRecordNumber && row.recordNumber ? `${row.recordNumber}` : ''}
                    </span>
                  </div>

                  <div className="pd-card-row">
                    <div className="pd-card-col">
                      <span className="pd-card-sub">
                        {t('principleDecisions.cards.responsible')}
                      </span>
                      {names && <span title={names}>{names}</span>}
                    </div>
                    <div className="pd-card-col-end">
                      <span className="pd-card-sub">
                        {t('principleDecisions.cards.decisionDate')}
                      </span>

                      <span>{formatDate(row.creDate)}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          {!list.isLoading && total > 0 && (
            <Paginator
              first={first}
              rows={rows}
              totalRecords={total}
              onPageChange={(e: PaginatorPageChangeEvent) => {
                setFirst(e.first)
                setRows(e.rows)
              }}
              template="PrevPageLink CurrentPageReport NextPageLink"
              currentPageReportTemplate={t('common.pageReport')}
              className="pd-cards-paginator"
            />
          )}
        </div>
      ) : (
        /* `tk-table-fit`: tablo **kaydırmasız** sığar (kullanıcı isteği 2026-08-10: uzun prensip kararı
            açıklaması tabloyu yana kaydırıyordu; açıklama alt satıra insin). Aksiyonlar listesiyle aynı
            mekanizma: düzen `fixed` olduğu için kolon genişlikleri **yüzde** verilir (toplam 100) ve
            yüzdeler oransal normalize edildiğinden yetkiye göre gizlenen kolonlar (Kayıt No) ya da
            daralan işlem kolonu toplamı bozduğunda da taşma olmaz.

            En geniş pay Açıklama'ya: listenin birincil metni odur ve tek satıra sığmayacağı bilinir.
            Sarması gerekenler `tk-cell-wrap` ile açıkça işaretlenir (Açıklama, Toplantı Grubu, Sorumlu
            — hepsi boşluk içerdiği için kelime sınırında sarar). (2026-08-12: Koordine kolonu kaldırıldı;
            boşalan %12 Açıklama'ya eklendi, toplam yine 100.)

            **Prensip Karar No da sarar, kırpılmaz** (kullanıcı isteği 2026-08-10: "uzunsa ... koyma"):
            numara bir kimliktir, yarısı gizlenirse işe yaramaz. Tarih gibi diğer atomik değerlerden farkı,
            bileşenlerinin **tire ile ayrılmış** olmasıdır (`PK-2026-0145-01`) — tire bir satır sonu fırsatı
            olduğundan sarma tirenin ardından olur, harf/rakam ortasından bölünme oluşmaz. Payı da (%15)
            tek satıra sığacak şekilde verildi; sarma yalnız dar ekranda devreye giren emniyet. Oluşturma
            Tarihi ise (ayırma noktası olmayan `07.08.2026`) tek satır + ellipsis varsayılanında kalır. */
        <DataTable
          className="tk-table-fit"
          value={items}
          loading={list.isLoading}
          lazy
          first={first}
          rows={rows}
          totalRecords={total}
          onPage={(e) => {
            setFirst(e.first)
            setRows(e.rows)
          }}
          onSort={(e) => {
            setSortField(e.sortField)
            setSortOrder(e.sortOrder)
          }}
          sortField={sortField}
          sortOrder={sortOrder}
          emptyMessage={
            <EmptyState
              icon="pi pi-gavel"
              title={t('principleDecisions.empty.title')}
              description={t('principleDecisions.empty.desc')}
            />
          }
        >
          {showRecordNumber && (
            <Column
              field="recordNumber"
              header={t('principleDecisions.columns.number')}
              sortable
              headerStyle={{ width: '15%' }}
              bodyClassName="tk-cell-wrap"
            />
          )}
          {/* Toplantı grubu — Prensip Karar No'nun yanında. Toplantıya bağlı prensipte grup adı; toplantıya
            bağlı OLMAYAN (bağımsız) prensipte "Genel Prensip" gösterilir. Grup adı sunucu sıralamasında
            (SORT_EXPR) yer almadığından bu kolon sıralanabilir değil. */}
          <Column
            header={t('principleDecisions.columns.group')}
            headerStyle={{ width: '12%' }}
            bodyClassName="tk-cell-wrap"
            body={(row: PrincipleDecision) =>
              row.meetingGroupName || t('principleDecisions.columns.groupGeneral')
            }
          />
          <Column
            field="description"
            header={t('principleDecisions.columns.description')}
            sortable
            headerStyle={{ width: '38%' }}
            bodyClassName="tk-cell-strong tk-cell-wrap"
          />
          <Column
            header={t('principleDecisions.columns.responsible')}
            sortable
            sortField="responsible"
            headerStyle={{ width: '12%' }}
            bodyClassName="tk-cell-wrap"
            body={(row: PrincipleDecision) => (
              // Liste ile eklenen sorumlular LİSTE ADI altında gruplanır; üzerine gelinince kişiler
              // görünür (kullanıcı isteği 01.10.2026). Gösterim toplantı detayı tablolarıyla ORTAK.
              <ResponsibleGroups responsibles={row.responsibles} />
            )}
          />
          <Column
            field="creDate"
            header={t('principleDecisions.columns.createdDate')}
            sortable
            headerStyle={{ width: '10%' }}
            body={(row: PrincipleDecision) => formatDate(row.creDate)}
          />
          {/* İşlem kolonu: 108 ile Dosyalar düğmesi eklendi → yönetimde DÖRT düğme (~168px → %16),
              yalnız okuma yetkisinde İKİ düğme (Paydaşlar + Dosyalar, ~84px → %11). */}
          <Column
            header=""
            headerStyle={{ width: canManage ? '16%' : '11%' }}
            bodyStyle={{ textAlign: 'right' }}
            body={(row: PrincipleDecision) => (
              <div className="pd-row-actions">
                <button
                  type="button"
                  className="pd-row-btn"
                  aria-label={t('principleDecisions.actions.stakeholders')}
                  title={t('principleDecisions.actions.stakeholders')}
                  onClick={() => openStakeholders(row)}
                >
                  <i className="pi pi-users" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="pd-row-btn"
                  aria-label={t('principleDecisions.actions.files')}
                  title={t('principleDecisions.actions.files')}
                  onClick={() => setFilesPrinciple(row)}
                >
                  <i className="pi pi-paperclip" aria-hidden="true" />
                </button>
                {canManage && (
                  <>
                    <button
                      type="button"
                      className="pd-row-btn"
                      aria-label={t('principleDecisions.actions.edit')}
                      title={t('principleDecisions.actions.edit')}
                      onClick={() => openEdit(row)}
                    >
                      <i className="pi pi-pencil" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="pd-row-btn"
                      aria-label={t('principleDecisions.actions.delete')}
                      title={t('principleDecisions.actions.delete')}
                      onClick={() => onDelete(row)}
                    >
                      <i className="pi pi-trash" aria-hidden="true" />
                    </button>
                  </>
                )}
              </div>
            )}
          />
        </DataTable>
      )}
    </div>
  )
}
